import { useQueryClient } from '@tanstack/react-query';
import { getFreshToken } from '@/api/axios';
import { NotificationsEndpoints } from '@/api/endpoints/NotificationsEndpoints';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { type NotificationDto } from '@/app/models/NotificationDto';
import { type TaskInfoDto } from '@/app/models/TaskInfoDto';
import { ANNOUNCEMENTS_QUERY_KEY } from '@/app/modules/announcements/core/_hooks';
import { applyTaskEvent, refreshMyTasks } from '@/app/modules/task';
import { setNotificationStreamOpen } from '@/app/modules/notifications/core/streamStatus';
import { isAnnouncementNotification } from '@/app/modules/notifications/core/_utils';
import EventNotificationToast from '@/app/modules/notifications/components/EventNotificationToast';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { getProjectEnvVariables } from '@/shared/projectEnvVariables';

interface SSEListeners {
  onNotification: (notification: NotificationDto) => void;
  /** Evento nomeado `task`: a tarefa do usuário mudou de estado ou de etapa. */
  onTask: (task: TaskInfoDto<string>) => void;
  /** A cada conexão e reconexão; `false` quando o stream cai. */
  onStreamChange: (open: boolean) => void;
}

export const listenToNotificationsSSE = ({ onNotification, onTask, onStreamChange }: SSEListeners): (() => void) => {
  const projectEnvVariables = getProjectEnvVariables();
  const baseURL = projectEnvVariables.envVariables.VITE_APP_API_URL;
  const url = `${baseURL}${NotificationsEndpoints.stream}`;

  let eventSource: EventSource | null = null;
  let shouldReconnect = true;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  const maxReconnectAttempts = 10;
  const reconnectDelay = 30000; // 30 segundos

  const scheduleReconnect = () => {
    onStreamChange(false);

    // Only attempt to reconnect if we haven't exceeded max attempts
    if (shouldReconnect && reconnectAttempts < maxReconnectAttempts) {
      reconnectAttempts++;
      console.log(
        `SSE: Tentando reconectar em ${reconnectDelay}ms... (tentativa ${reconnectAttempts}/${maxReconnectAttempts})`,
      );

      reconnectTimeout = setTimeout(() => {
        if (shouldReconnect) {
          void connect();
        }
      }, reconnectDelay);
    } else if (reconnectAttempts >= maxReconnectAttempts) {
      console.error('SSE: Máximo de tentativas de reconexão atingido. Parando tentativas.');
      shouldReconnect = false;
    }
  };

  const connect = async () => {
    console.log(`SSE: Tentativa de conexão ${reconnectAttempts + 1}/${maxReconnectAttempts}`);
    // The backend checks the token only when the stream opens: an expired one answers an empty 200.
    let token: string | undefined;
    try {
      token = await getFreshToken();
    } catch (error) {
      console.error('SSE: Não foi possível renovar o token.', error);
      scheduleReconnect();
      return;
    }
    if (!shouldReconnect || !token) return;

    eventSource = new EventSource(`${url}?token=${token}`);

    eventSource.onmessage = (event: MessageEvent) => {
      try {
        const eventData = event.data as string;
        if (!eventData || eventData.trim() === '') {
          return;
        }

        const data = JSON.parse(eventData) as NotificationDto;

        if (!data) {
          return;
        }

        // Reset reconnect attempts on successful message
        reconnectAttempts = 0;
        onNotification(data);
      } catch (error) {
        console.error('SSE: Erro ao processar (JSON.parse) o evento SSE:', error);
        console.error('SSE: Dados recebidos:', event.data);
        console.error('SSE: Tipo dos dados:', typeof event.data);
      }
    };

    eventSource.addEventListener('task', (event: MessageEvent<string>) => {
      try {
        onTask(JSON.parse(event.data) as TaskInfoDto<string>);
      } catch (error) {
        console.error('SSE: Erro ao processar o evento `task`:', error, event.data);
      }
    });

    eventSource.onopen = () => {
      console.log('SSE: Conexão estabelecida com sucesso.');
      reconnectAttempts = 0; // Reset attempts on successful connection
      onStreamChange(true);
    };

    eventSource.onerror = (error) => {
      console.error('SSE: Erro na conexão. EventSource.readyState:', eventSource?.readyState);
      console.error('SSE: Detalhes do erro:', error);

      // Close the current connection to prevent automatic reconnection
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }

      scheduleReconnect();
    };
  };

  // Start the initial connection
  void connect();

  // Return cleanup function
  return () => {
    console.log('SSE: Fechando conexão EventSource (cleanup do Context Provider).');
    shouldReconnect = false;

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
  };
};

interface NotificationsContextType {
  notifications: NotificationDto[];
  hasUnread: boolean;
  hasUnseenNotifications: boolean;
  markNotificationsAsSeen: () => void;
  // Se você tiver funções para marcar como lida, elas iriam aqui:
  // markAsRead: (id: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [hasUnseenNotifications, setHasUnseenNotifications] = useState(false);

  const hasUnread = useMemo(() => {
    return notifications.some((notification) => !notification.read);
  }, [notifications]);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }

    const handleNewNotification = (newNotification: NotificationDto) => {
      // O comunicado tem aba e modal próprios: o aviso só manda reconferir os pendentes.
      if (isAnnouncementNotification(newNotification)) {
        void queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
        return;
      }

      if (!newNotification.title) {
        console.log(newNotification);
        return;
      }

      setNotifications((prevNotifications) => [newNotification, ...prevNotifications]);
      setHasUnseenNotifications(true);

      toast((props) => <EventNotificationToast {...props} notification={newNotification} />, {
        position: 'top-right',
        autoClose: 6000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: 'custom-notification-toast-wrapper',
        style: {
          padding: 0,
          background: 'transparent',
          boxShadow: 'none',
        },
      });
    };

    const cleanup = listenToNotificationsSSE({
      onNotification: handleNewNotification,
      // O SSE só acelera: a verdade é a lista, consultada de novo a cada (re)conexão.
      onTask: (task) => applyTaskEvent(queryClient, task),
      onStreamChange: (open) => {
        setNotificationStreamOpen(open);
        if (open) void refreshMyTasks(queryClient);
      },
    });

    return () => {
      cleanup();
      setNotificationStreamOpen(false);
    };
  }, [auth, queryClient]);

  const markNotificationsAsSeen = () => {
    setHasUnseenNotifications(false);
  };

  const values = useMemo(
    () => ({
      notifications,
      hasUnread,
      hasUnseenNotifications,
      markNotificationsAsSeen,
    }),
    [notifications, hasUnread, hasUnseenNotifications],
  );

  return <NotificationsContext.Provider value={values}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications deve ser usado dentro de um NotificationsProvider');
  }
  return context;
};
